import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/services/api";
import type { InvoiceActionRequest, InvoiceActionResponse, RevisionRequestPayload } from "../types/action";
import type { UpdateInvoicePayload, UploadInvoiceResponse } from "../types/invoice";
import { invoiceKeys, INVOICE_BASE_URL } from "./queryKeys";

// --- performAction ---

export async function performAction(
  id: number,
  payload: InvoiceActionRequest
): Promise<InvoiceActionResponse> {
  return api.post(`${INVOICE_BASE_URL}/${id}/action`, payload);
}

export function useInvoiceAction(invoiceId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: InvoiceActionRequest) => performAction(invoiceId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries(invoiceKeys.detail(invoiceId));
      queryClient.invalidateQueries(invoiceKeys.lists());
      queryClient.invalidateQueries(invoiceKeys.summary());
    },
  });
}

// --- requestRevision ---

export async function requestRevision(
  id: number,
  data: RevisionRequestPayload
): Promise<InvoiceActionResponse> {
  return api.post(`${INVOICE_BASE_URL}/${id}/revision`, data);
}

export function useRequestRevision(invoiceId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: RevisionRequestPayload) => requestRevision(invoiceId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(invoiceKeys.detail(invoiceId));
      queryClient.invalidateQueries(invoiceKeys.lists());
    },
  });
}

// --- updateInvoice (PATCH) ---

export async function updateInvoice(
  id: number,
  payload: UpdateInvoicePayload
): Promise<unknown> {
  return api.patch(`${INVOICE_BASE_URL}/${id}`, payload);
}

export function useUpdateInvoice(invoiceId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateInvoicePayload) => updateInvoice(invoiceId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries(invoiceKeys.detail(invoiceId));
      queryClient.invalidateQueries(invoiceKeys.lists());
      queryClient.invalidateQueries(invoiceKeys.summary());
    },
  });
}

// --- deleteInvoice ---

export async function deleteInvoice(id: number): Promise<void> {
  return api.remove(`${INVOICE_BASE_URL}/${id}`);
}

export function useDeleteInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteInvoice(id),
    onSuccess: () => {
      queryClient.invalidateQueries(invoiceKeys.lists());
      queryClient.invalidateQueries(invoiceKeys.summary());
    },
  });
}

// --- uploadInvoice ---

export async function uploadInvoice(formData: FormData): Promise<UploadInvoiceResponse> {
  return api.post(`${INVOICE_BASE_URL}/upload`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
}

export function useUploadInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (formData: FormData) => uploadInvoice(formData),
    onSuccess: () => {
      queryClient.invalidateQueries(invoiceKeys.lists());
      queryClient.invalidateQueries(invoiceKeys.summary());
    },
  });
}
